// The model's structure, filled with the scenario's values (TX-US-0026). What is drawn - the
// timeline points, the payment terms and what triggers each, the cost lines and how each is
// charged - is the model's; only the figures are the scenario's, so a field the model gains shows
// on every scenario, empty until someone fills it. The one choice of structure a scenario makes is
// the point each payment term is due from (TX-TSK-0128, TX-TSK-0129). `update(path, value)` writes
// one value back.

import Icon from '../ui/Icon.jsx';
import {
  COMMISSION_WHOLE, commissionTotal, costLines, dueFrom, dueFromChoices, FIXED_DATE, isFixedStart,
  pointLabel, RANGES, reanchor, reanchorStart, startChoices, startLabel, timelineAnchors,
} from './inputs.js';
import SliderField from './SliderField.jsx';

const BASIS_LABELS = {
  PER_MT: 'per MT',
  PCT_OF_PURCHASE: '% of purchase',
  PER_UNIT: 'per unit',
  PER_MT_PER_DAY: 'per MT per day',
  PER_MT_PER_DAY_FOR_DAYS: 'per MT per day',
  FIXED: 'fixed',
};

function TextField({ label, value, onChange, type = 'text' }) {
  return (
    <label className={value ? 'econ-text' : 'econ-text is-empty'}>
      <span>{label}</span>
      <input type={type} value={value ?? ''}
             onChange={(event) => onChange(event.target.value || null)} />
    </label>
  );
}

// When a payment falls due: a choice of the point, and the days from it. `onChange(field, value)`
// writes `due_from` or `delay_days` - to one term, or to every term of a grouped charge.
function DueFrom({ structure, term, values, onChange, buyer = false }) {
  const point = dueFrom(term, values);
  return (
    <>
      <label className="econ-text">
        <span>Due from</span>
        <select value={point} onChange={(event) => onChange('due_from', event.target.value)}>
          {dueFromChoices(structure, { buyer }).map((p) => (
            <option key={p.key} value={p.key}>{p.label}</option>
          ))}
        </select>
      </label>
      <SliderField label={`Days from ${pointLabel(structure, point)}`} value={values?.delay_days}
                   range={RANGES.delay} onChange={(v) => onChange('delay_days', v)} />
    </>
  );
}

// One term of a payment: a share, due some days from the point the scenario chooses - unless its
// charge chooses for it (`timed={false}`). A buyer tranche (`commission`) may also be due from the
// buyer PO's start, and releases its share of the buyer PO's commission some days after it is
// received; a tranche with no share releases none.
function Term({ structure, term, values, path, update, commission = false, timed = true }) {
  return (
    <fieldset className="econ-instalment">
      <legend>{term.label}</legend>
      <SliderField label="Share" value={values?.share} range={RANGES.share}
                   onChange={(v) => update([...path, 'share'], v)} />
      {timed && (
        <DueFrom structure={structure} term={term} values={values} buyer={commission}
                 onChange={(field, v) => update([...path, field], v)} />
      )}
      {commission && (
        <>
          <SliderField label="Commission share" value={values?.commission_share}
                       range={RANGES.share}
                       onChange={(v) => update([...path, 'commission_share'], v)} />
          <SliderField label="Commission days after payment" value={values?.commission_delay_days}
                       range={RANGES.commissionDelay}
                       onChange={(v) => update([...path, 'commission_delay_days'], v)} />
        </>
      )}
    </fieldset>
  );
}

function Terms({ structure, terms, values, path, update, ...kind }) {
  return terms.map((term) => (
    <Term key={term.key} structure={structure} term={term} values={values?.[term.key]}
          path={[...path, term.key]} update={update} {...kind} />
  ));
}

function BuyerTerms({ structure, values, update }) {
  const total = commissionTotal(structure, values);
  return (
    <details className="econ-cost">
      <summary>
        Buyer payment terms
        {total !== COMMISSION_WHOLE && (
          <span className="error"> · commissions {total}%, must be 100%</span>
        )}
      </summary>
      <Terms structure={structure} terms={structure.sale_terms} values={values.sale?.terms}
             path={['sale', 'terms']} update={update} commission />
    </details>
  );
}

function Cost({ structure, cost, values, path, update, timed = true }) {
  return (
    <details className="econ-cost">
      <summary>
        {cost.label} <span className="muted">· {BASIS_LABELS[cost.basis]}</span>
      </summary>
      <SliderField label="Rate" value={values?.rate} range={RANGES[cost.basis]}
                   onChange={(v) => update([...path, 'rate'], v)} />
      {cost.basis === 'PER_UNIT' && <p className="hint">Per unit of {cost.unit_size_mt} MT.</p>}
      {cost.basis === 'PER_MT_PER_DAY_FOR_DAYS' && (
        <SliderField label="Number of days" value={values?.days} range={RANGES.days}
                     onChange={(v) => update([...path, 'days'], v)} />
      )}
      {cost.basis === 'PER_MT_PER_DAY' && (
        <p className="hint">
          Charged for every day from {pointLabel(structure, cost.period_from)} to{' '}
          {pointLabel(structure, cost.period_to)}.
        </p>
      )}
      <Terms structure={structure} terms={cost.terms} values={values?.terms}
             path={[...path, 'terms']} update={update} timed={timed} />
    </details>
  );
}

// A group of costs charged as one: one Due from and one Days from, shown from its first cost's
// first term and written to every term of every cost in it, so the charge stays one event.
function Charge({ structure, group, costs, values, update }) {
  const [first] = costs;
  const term = first.terms[0];
  const write = (field, value) => costs.forEach((cost) => cost.terms.forEach((t) => (
    update(['costs', cost.key, 'terms', t.key, field], value)
  )));
  return (
    <fieldset className="econ-instalment econ-charge">
      <legend>{group}</legend>
      <p className="hint">Its {costs.length} costs are paid together, as one charge.</p>
      <DueFrom structure={structure} term={term}
               values={values.costs?.[first.key]?.terms?.[term.key]} onChange={write} />
      {costs.map((cost) => (
        <Cost key={cost.key} structure={structure} cost={cost} values={values.costs?.[cost.key]}
              path={['costs', cost.key]} update={update} timed={false} />
      ))}
    </fieldset>
  );
}

function TimelinePoint({ label, point, anchors, onAnchor, path, update }) {
  const fixed = point?.after == null;
  const anchor = anchors.find((a) => a.key === point?.after);
  return (
    <fieldset className="econ-instalment">
      <legend>{label}</legend>
      <label className="econ-text">
        <span>Dated</span>
        <select value={fixed ? FIXED_DATE : point.after}
                onChange={(event) => onAnchor(event.target.value)}>
          <option value={FIXED_DATE}>On a fixed date</option>
          {anchors.map((a) => <option key={a.key} value={a.key}>From {a.label}</option>)}
        </select>
      </label>
      {fixed ? (
        <TextField label="Date" type="date" value={point?.on_date}
                   onChange={(v) => update([...path, 'on_date'], v)} />
      ) : (
        <SliderField label={`Days from ${anchor?.label ?? point.after}`}
                     value={point.offset_days} range={RANGES.point}
                     onChange={(v) => update([...path, 'offset_days'], v)} />
      )}
    </fieldset>
  );
}

// The calculated date of each point for the first supplier PO, by key - what re-anchoring keeps.
const pointDates = (values, dated) => Object.fromEntries(
  dated.filter((m) => m.source === values.supply?.[0]?.label).map((m) => [m.key, m.date]),
);

function Timeline({ structure, values, dated, update }) {
  return (
    <details className="econ-group" open>
      <summary>Timeline <span className="muted">· from each supplier PO&apos;s start</span></summary>
      <div className="econ-group-body">
        {structure.points.map(({ key, label }) => (
          <TimelinePoint key={key} label={label} point={values.timeline?.[key]}
                         anchors={timelineAnchors(structure, values.timeline, key)}
                         path={['timeline', key]} update={update}
                         onAnchor={(anchor) => update(
                           ['timeline', key],
                           reanchor(values.timeline?.[key], key, anchor, pointDates(values, dated)),
                         )} />
        ))}
      </div>
    </details>
  );
}

// A supplier PO starts on a fixed date, or days from the first buyer payment or from any supplier
// PO listed before it (TX-TSK-0131). `startDates` - each PO's calculated start and the first
// payment's - keep the day it starts when it is re-anchored. Editing its start clears any start
// saved from the buyer PO's start, which the release before reads.
function SupplyOrder({ structure, supply, order, index, startDates, canRemove, onRemove, update }) {
  const path = ['supply', index];
  const fixed = isFixedStart(order);
  const setStart = (field, value) => update(path, { ...order, [field]: value, start_offset_days: null });
  return (
    <details className="econ-group">
      <summary>
        {order.label}
        <span className="muted"> · {order.quantity_mt ?? '—'} MT @ ${order.price_per_mt ?? '—'}</span>
      </summary>
      <div className="econ-group-body">
        <TextField label="Name" value={order.label}
                   onChange={(v) => update([...path, 'label'], v ?? '')} />
        <TextField label="Supplier" value={order.supplier}
                   onChange={(v) => update([...path, 'supplier'], v)} />
        <label className="econ-text">
          <span>Starts</span>
          <select value={fixed ? FIXED_DATE : order.start_from}
                  onChange={(event) => update(
                    path, reanchorStart(order, index, event.target.value, startDates),
                  )}>
            {startChoices(supply, index).map((c) => (
              <option key={c.key} value={c.key}>{c.label}</option>
            ))}
          </select>
        </label>
        {fixed ? (
          <TextField label="Start date" type="date" value={order.start_on_date}
                     onChange={(v) => setStart('start_on_date', v)} />
        ) : (
          <SliderField label={`Days from ${startLabel(supply, order)}`} value={order.start_days}
                       range={RANGES.startOffset}
                       onChange={(v) => setStart('start_days', v)} />
        )}
        <SliderField label="Quantity" value={order.quantity_mt} range={RANGES.quantity}
                     onChange={(v) => update([...path, 'quantity_mt'], v)} />
        <SliderField label="Price" value={order.price_per_mt} range={RANGES.price}
                     onChange={(v) => update([...path, 'price_per_mt'], v)} />

        <h4>Supplier payment terms</h4>
        <Terms structure={structure} terms={structure.supply_terms} values={order.terms}
               path={[...path, 'terms']} update={update} />

        {canRemove && (
          <button type="button" className="econ-remove" onClick={onRemove}>
            <Icon name="trash" /> Remove {order.label}
          </button>
        )}
      </div>
    </details>
  );
}

export default function InputsPanel({
  structure, values, dated, startDates, update, onAddSupplyOrder, onRemoveSupplyOrder,
}) {
  const sale = values.sale ?? {};
  const finance = values.finance ?? {};
  const supply = values.supply ?? [];
  return (
    <div className="econ-inputs">
      <details className="econ-group" open>
        <summary>Buyer PO <span className="muted">· {sale.buyer ?? '—'}</span></summary>
        <div className="econ-group-body">
          <TextField label="Buyer" value={sale.buyer}
                     onChange={(v) => update(['sale', 'buyer'], v)} />
          <TextField label="PO start date" type="date" value={values.start_date}
                     onChange={(v) => update(['start_date'], v)} />
          <SliderField label="Quantity" value={sale.quantity_mt} range={RANGES.quantity}
                       onChange={(v) => update(['sale', 'quantity_mt'], v)} />
          <SliderField label="Sale price" value={sale.price_per_mt} range={RANGES.price}
                       onChange={(v) => update(['sale', 'price_per_mt'], v)} />
          <SliderField label="Commission, % of sale price" value={sale.commission_rate}
                       range={RANGES.commissionRate}
                       onChange={(v) => update(['sale', 'commission_rate'], v)} />
          <BuyerTerms structure={structure} values={values} update={update} />
        </div>
      </details>

      <Timeline structure={structure} values={values} dated={dated} update={update} />

      {supply.map((order, i) => (
        <SupplyOrder key={i} structure={structure} supply={supply} order={order} index={i}
                     update={update} startDates={startDates} canRemove={supply.length > 1}
                     onRemove={() => onRemoveSupplyOrder(i)} />
      ))}
      <button type="button" onClick={onAddSupplyOrder}>
        <Icon name="plus" /> Add supplier PO
      </button>

      <details className="econ-group">
        <summary>Costs <span className="muted">· for every supplier PO, from its own start</span></summary>
        <div className="econ-group-body">
          {costLines(structure).map(({ group, costs }) => (group ? (
            <Charge key={group} structure={structure} group={group} costs={costs}
                    values={values} update={update} />
          ) : (
            <Cost key={costs[0].key} structure={structure} cost={costs[0]}
                  values={values.costs?.[costs[0].key]} path={['costs', costs[0].key]}
                  update={update} />
          )))}
        </div>
      </details>

      <details className="econ-group" open>
        <summary>Finance</summary>
        <div className="econ-group-body">
          <SliderField label="Finance rate (annual)" value={finance.annual_rate}
                       range={RANGES.annualRate}
                       onChange={(v) => update(['finance', 'annual_rate'], v)} />
          <SliderField label="Facility limit" value={finance.facility_limit}
                       range={RANGES.facility}
                       onChange={(v) => update(['finance', 'facility_limit'], v)} />
        </div>
      </details>
    </div>
  );
}
